/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  PencilIcon,
  TrashIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  ArrowDownTrayIcon,
  Square3Stack3DIcon,
  MinusIcon,
  ArrowRightIcon,
  ChatBubbleOvalLeftIcon,
} from '@heroicons/react/24/outline';
import { safeStorage } from '../utils/safeStorage';

interface Point {
  x: number;
  y: number;
}

type Tool = 'select' | 'pen' | 'line' | 'arrow' | 'rectangle' | 'ellipse' | 'text' | 'eraser';

interface DrawingElement {
  id: string;
  type: Tool;
  points: Point[];
  color: string;
  lineWidth: number;
  text?: string;
  x?: number;  // Offset for dragging
  y?: number;  // Offset for dragging
  width?: number;
  height?: number;
}

const COLORS = [
  '#F97316', // Primary orange
  '#EF4444', // Red
  '#22C55E', // Green
  '#3B82F6', // Blue
  '#A855F7', // Purple
  '#EC4899', // Pink
  '#FFFFFF', // White
  '#6B7280', // Gray
  '#000000', // Black
];

const LINE_WIDTHS = [1, 2, 4, 6];

const QuickNotes: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState<Tool>('pen');
  const [color, setColor] = useState(COLORS[0]);
  const [lineWidth, setLineWidth] = useState(LINE_WIDTHS[1]);
  const [elements, setElements] = useState<DrawingElement[]>([]);
  const [currentElement, setCurrentElement] = useState<DrawingElement | null>(null);
  const [undoStack, setUndoStack] = useState<DrawingElement[][]>([]);
  const [redoStack, setRedoStack] = useState<DrawingElement[][]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  const [draggedElement, setDraggedElement] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null);

  // Generate unique ID
  const generateId = () => Math.random().toString(36).substr(2, 9);

  // Load saved data from localStorage
  useEffect(() => {
    const savedElements = safeStorage.getItem('quicknotes-elements');
    if (savedElements) {
      try {
        setElements(JSON.parse(savedElements));
      } catch (e) {
        console.error('Failed to load canvas data:', e);
      }
    }
  }, []);

  // Save elements to localStorage
  useEffect(() => {
    safeStorage.setItem('quicknotes-elements', JSON.stringify(elements));
  }, [elements]);

  // Draw all elements on canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas with dark background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw dot grid pattern (Excalidraw style)
    ctx.fillStyle = '#2a2a4a';
    const gridSize = 20;
    for (let x = gridSize; x < canvas.width; x += gridSize) {
      for (let y = gridSize; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.arc(x, y, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw all elements
    [...elements, currentElement].filter(Boolean).forEach((element) => {
      if (!element) return;
      drawElement(ctx, element, element.id === selectedElement);
    });
  }, [elements, currentElement, selectedElement]);

  const drawElement = (ctx: CanvasRenderingContext2D, element: DrawingElement, isSelected: boolean = false) => {
    ctx.strokeStyle = element.type === 'eraser' ? '#1a1a2e' : element.color;
    ctx.fillStyle = element.color;
    ctx.lineWidth = element.type === 'eraser' ? element.lineWidth * 4 : element.lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // Apply offset for dragged elements
    const offsetX = element.x || 0;
    const offsetY = element.y || 0;

    switch (element.type) {
      case 'pen':
      case 'eraser':
        if (element.points.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(element.points[0].x + offsetX, element.points[0].y + offsetY);
        for (let i = 1; i < element.points.length; i++) {
          ctx.lineTo(element.points[i].x + offsetX, element.points[i].y + offsetY);
        }
        ctx.stroke();
        break;

      case 'line':
        if (element.points.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(element.points[0].x + offsetX, element.points[0].y + offsetY);
        ctx.lineTo(element.points[element.points.length - 1].x + offsetX, element.points[element.points.length - 1].y + offsetY);
        ctx.stroke();
        break;

      case 'arrow':
        if (element.points.length < 2) return;
        const start = { x: element.points[0].x + offsetX, y: element.points[0].y + offsetY };
        const end = { x: element.points[element.points.length - 1].x + offsetX, y: element.points[element.points.length - 1].y + offsetY };

        // Draw line
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();

        // Draw arrowhead
        const angle = Math.atan2(end.y - start.y, end.x - start.x);
        const headLength = 15;
        ctx.beginPath();
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(
          end.x - headLength * Math.cos(angle - Math.PI / 6),
          end.y - headLength * Math.sin(angle - Math.PI / 6)
        );
        ctx.moveTo(end.x, end.y);
        ctx.lineTo(
          end.x - headLength * Math.cos(angle + Math.PI / 6),
          end.y - headLength * Math.sin(angle + Math.PI / 6)
        );
        ctx.stroke();
        break;

      case 'rectangle':
        if (element.points.length < 2) return;
        const rectStart = element.points[0];
        const rectEnd = element.points[element.points.length - 1];
        const width = rectEnd.x - rectStart.x;
        const height = rectEnd.y - rectStart.y;

        ctx.beginPath();
        ctx.strokeRect(rectStart.x + offsetX, rectStart.y + offsetY, width, height);
        break;

      case 'ellipse':
        if (element.points.length < 2) return;
        const ellipseStart = element.points[0];
        const ellipseEnd = element.points[element.points.length - 1];
        const centerX = (ellipseStart.x + ellipseEnd.x) / 2 + offsetX;
        const centerY = (ellipseStart.y + ellipseEnd.y) / 2 + offsetY;
        const radiusX = Math.abs(ellipseEnd.x - ellipseStart.x) / 2;
        const radiusY = Math.abs(ellipseEnd.y - ellipseStart.y) / 2;

        ctx.beginPath();
        ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, Math.PI * 2);
        ctx.stroke();
        break;

      case 'text':
        if (element.text) {
          ctx.font = `${element.lineWidth * 6}px "Segoe UI", system-ui, sans-serif`;
          const textX = element.points[0].x + offsetX;
          const textY = element.points[0].y + offsetY;
          ctx.fillText(element.text, textX, textY);

          // Draw selection box for text
          if (isSelected) {
            const metrics = ctx.measureText(element.text);
            ctx.strokeStyle = '#F97316';
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.strokeRect(textX - 4, textY - element.lineWidth * 5, metrics.width + 8, element.lineWidth * 6 + 4);
            ctx.setLineDash([]);
          }
        }
        break;
    }
  };

  // Find element at a point (for selection)
  const findElementAtPoint = (point: Point): DrawingElement | null => {
    // Check elements in reverse order (top to bottom)
    for (let i = elements.length - 1; i >= 0; i--) {
      const el = elements[i];
      const offsetX = el.x || 0;
      const offsetY = el.y || 0;

      if (el.type === 'text' && el.text) {
        const x = el.points[0].x + offsetX;
        const y = el.points[0].y + offsetY;
        // Approximate text bounds
        const textWidth = el.text.length * el.lineWidth * 3;
        const textHeight = el.lineWidth * 6;
        if (point.x >= x - 5 && point.x <= x + textWidth + 5 &&
            point.y >= y - textHeight && point.y <= y + 5) {
          return el;
        }
      }
      // Add hit detection for other element types if needed
    }
    return null;
  };

  // Resize canvas to fit container
  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;

      const rect = container.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      drawCanvas();
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [drawCanvas, isFullscreen]);

  // Redraw canvas when elements change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  const getCanvasPoint = (e: React.MouseEvent | React.TouchEvent): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();

    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }

    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const point = getCanvasPoint(e);

    // Handle select tool
    if (tool === 'select') {
      const el = findElementAtPoint(point);
      if (el) {
        setSelectedElement(el.id);
        const offsetX = el.x || 0;
        const offsetY = el.y || 0;
        setDraggedElement({
          id: el.id,
          offsetX: point.x - offsetX,
          offsetY: point.y - offsetY,
        });
      } else {
        setSelectedElement(null);
      }
      return;
    }

    if (tool === 'text') {
      const text = prompt('Enter text:');
      if (text) {
        const newElement: DrawingElement = {
          id: generateId(),
          type: 'text',
          points: [point],
          color,
          lineWidth,
          text,
          x: 0,
          y: 0,
        };
        setUndoStack([...undoStack, elements]);
        setRedoStack([]);
        setElements([...elements, newElement]);
      }
      return;
    }

    setIsDrawing(true);
    setStartPoint(point);
    setCurrentElement({
      id: generateId(),
      type: tool,
      points: [point],
      color,
      lineWidth,
      x: 0,
      y: 0,
    });
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const point = getCanvasPoint(e);

    // Handle dragging selected element
    if (draggedElement) {
      setElements(elements.map(el =>
        el.id === draggedElement.id
          ? { ...el, x: point.x - draggedElement.offsetX, y: point.y - draggedElement.offsetY }
          : el
      ));
      return;
    }

    if (!isDrawing || !currentElement || !startPoint) return;

    if (tool === 'pen' || tool === 'eraser') {
      setCurrentElement({
        ...currentElement,
        points: [...currentElement.points, point],
      });
    } else {
      // For shapes, only keep start and current point
      setCurrentElement({
        ...currentElement,
        points: [startPoint, point],
      });
    }
  };

  const stopDrawing = () => {
    if (draggedElement) {
      setDraggedElement(null);
      return;
    }

    if (isDrawing && currentElement && currentElement.points.length > 1) {
      setUndoStack([...undoStack, elements]);
      setRedoStack([]);
      setElements([...elements, currentElement]);
    }
    setIsDrawing(false);
    setCurrentElement(null);
    setStartPoint(null);
  };

  const undo = () => {
    if (undoStack.length === 0) return;
    const previousElements = undoStack[undoStack.length - 1];
    setRedoStack([...redoStack, elements]);
    setElements(previousElements);
    setUndoStack(undoStack.slice(0, -1));
  };

  const redo = () => {
    if (redoStack.length === 0) return;
    const nextElements = redoStack[redoStack.length - 1];
    setUndoStack([...undoStack, elements]);
    setElements(nextElements);
    setRedoStack(redoStack.slice(0, -1));
  };

  const clearCanvas = () => {
    if (elements.length === 0) return;
    setUndoStack([...undoStack, elements]);
    setRedoStack([]);
    setElements([]);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const exportCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `quicknotes-${Date.now()}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const tools: { id: Tool; icon: React.ReactNode; label: string }[] = [
    { id: 'select', icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
      </svg>
    ), label: 'Select & Move' },
    { id: 'pen', icon: <PencilIcon className="w-4 h-4" />, label: 'Pen' },
    { id: 'line', icon: <MinusIcon className="w-4 h-4" />, label: 'Line' },
    { id: 'arrow', icon: <ArrowRightIcon className="w-4 h-4" />, label: 'Arrow' },
    { id: 'rectangle', icon: <Square3Stack3DIcon className="w-4 h-4" />, label: 'Rectangle' },
    { id: 'ellipse', icon: <span className="w-4 h-4 border-2 border-current rounded-full inline-block" />, label: 'Ellipse' },
    { id: 'text', icon: <ChatBubbleOvalLeftIcon className="w-4 h-4" />, label: 'Text' },
    {
      id: 'eraser',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 20H7l-4-4 10-10 7 7-6 6 3 3" />
        </svg>
      ),
      label: 'Eraser',
    },
  ];

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-gray-900 flex flex-col' : ''}`}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isFullscreen ? 'px-4 py-3 border-b border-gray-700 flex-shrink-0' : 'mb-4'}`}>
        <div>
          <h1 className="text-xl font-bold text-white">Quick Notes</h1>
          <p className="text-gray-400 text-sm">
            Whiteboard for notes, diagrams & memory maps
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportCanvas}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-gray-300 hover:text-white rounded-lg text-sm transition-colors"
            title="Export as PNG"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="w-5 h-5" />
            ) : (
              <ArrowsPointingOutIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Canvas Container */}
      <div className={`bg-gray-900 rounded-xl border border-gray-700 overflow-hidden ${isFullscreen ? 'flex-1 mx-4 mb-4 flex flex-col' : ''}`}>
        {/* Toolbar */}
        <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between flex-wrap gap-2 bg-gray-800/50">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Tools */}
            <div className="flex items-center gap-0.5 bg-gray-900 rounded-lg p-1">
              {tools.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTool(t.id)}
                  className={`p-2 rounded-md transition-colors ${
                    tool === t.id
                      ? 'bg-primary-500 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                  title={t.label}
                >
                  {t.icon}
                </button>
              ))}
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-700" />

            {/* Colors */}
            <div className="flex items-center gap-1">
              {COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-5 h-5 rounded-full transition-transform border-2 ${
                    color === c ? 'border-white scale-125' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-700" />

            {/* Line Width */}
            <div className="flex items-center gap-1 bg-gray-900 rounded-lg p-1">
              {LINE_WIDTHS.map((w) => (
                <button
                  key={w}
                  onClick={() => setLineWidth(w)}
                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${
                    lineWidth === w
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                  title={`${w}px`}
                >
                  <div
                    className="rounded-full bg-current"
                    style={{ width: w * 2 + 2, height: w * 2 + 2 }}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-1">
            {/* Undo/Redo */}
            <button
              onClick={undo}
              disabled={undoStack.length === 0}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Undo (Ctrl+Z)"
            >
              <ArrowUturnLeftIcon className="w-4 h-4" />
            </button>
            <button
              onClick={redo}
              disabled={redoStack.length === 0}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Redo (Ctrl+Y)"
            >
              <ArrowUturnRightIcon className="w-4 h-4" />
            </button>

            {/* Divider */}
            <div className="h-6 w-px bg-gray-700 mx-1" />

            {/* Clear */}
            <button
              onClick={clearCanvas}
              disabled={elements.length === 0}
              className="flex items-center gap-1.5 px-2 py-1.5 text-red-400 hover:text-red-300 hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Clear canvas"
            >
              <TrashIcon className="w-4 h-4" />
              <span className="text-sm hidden sm:inline">Clear</span>
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div
          ref={containerRef}
          className={`relative ${isFullscreen ? 'flex-1' : 'h-[calc(100vh-280px)] min-h-[400px]'}`}
        >
          <canvas
            ref={canvasRef}
            className={`absolute inset-0 ${
              tool === 'select' ? 'cursor-move' :
              tool === 'text' ? 'cursor-text' :
              tool === 'eraser' ? 'cursor-cell' :
              'cursor-crosshair'
            } touch-none`}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </div>
      </div>

      {/* Footer */}
      <div className={`flex items-center justify-between text-xs text-gray-500 mt-2 ${isFullscreen ? 'px-4 pb-2' : ''}`}>
        <span>Auto-saved to browser</span>
        <span>{elements.length} elements</span>
      </div>
    </div>
  );
};

export default QuickNotes;
